// src/pages/CheckoutSuccess.js — Página de Parabéns pós-pagamento
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Container, Typography, Button, Stack, Fade,
  Accordion, AccordionSummary, AccordionDetails, Chip, Divider, Avatar
} from '@mui/material';
import { FiChevronDown, FiGrid, FiMessageSquare, FiUsers, FiLogOut } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br';

// ============================================================
// CHECKLIST ANIMADO — O que acontece agora
// ============================================================
const CHECKLIST = [
  { label: 'Pagamento confirmado e processado pela Stripe', delay: 300 },
  { label: 'Conta ativada com sucesso no Cetto System', delay: 800 },
  { label: 'Dados enviados para nossa equipe de engenharia', delay: 1400 },
  { label: 'Processo de criação do App na Meta iniciado', delay: 2000 },
  { label: 'Você receberá um e-mail com as próximas etapas', delay: 2600 },
];

// ============================================================
// FAQ — Perguntas e respostas pré-prontas
// ============================================================
const FAQ = [
  {
    question: 'Em quanto tempo terei um chip disponível para uso?',
    answer:
      'Em média de 10 a 15 dias. Após a confirmação do pagamento, nossa equipe entra em contato com a Meta no mesmo dia para iniciar o processo burocrático de aprovação. O prazo final depende 100% da Meta — fazemos tudo para agilizar ao máximo.',
  },
  {
    question: 'Como funciona o sistema de Templates?',
    answer:
      'Templates são mensagens pré-aprovadas pela Meta, usadas para iniciar conversas com clientes que ainda não interagiram com você nas últimas 24 horas. Seu plano já inclui créditos de templates mensais. Cada envio de template consome 1 crédito. Você pode adquirir pacotes extras a qualquer momento pelo painel.',
  },
  {
    question: 'Quais são os benefícios do meu plano?',
    answer:
      'No plano Starter você tem acesso a 1 chip WhatsApp Business API, 1 agente + 1 supervisor, monitoramento de saúde do chip 24/7, histórico de conversas e suporte por e-mail. Nos planos Business e Enterprise, os limites de chips, agentes e templates são expandidos significativamente.',
  },
  {
    question: 'Posso cancelar a qualquer momento?',
    answer:
      'Sim. Você pode cancelar sua assinatura a qualquer momento pelo painel em "Gerenciar Assinatura". Após o cancelamento, você mantém acesso até o final do período já pago. Não há multa ou fidelidade.',
  },
  {
    question: 'O que é o BM ID (Gerenciador de Negócios Meta)?',
    answer:
      'O Business Manager ID é o identificador da sua conta no Gerenciador de Negócios da Meta (Facebook). Ele é necessário para vincular seu número de WhatsApp à API oficial. Nossa equipe pode ajudá-lo a localizar ou criar essa conta durante o processo de onboarding.',
  },
  {
    question: 'Quando minha conta estará 100% operacional?',
    answer:
      'Após a aprovação da Meta (1 a 5 dias úteis), nossa equipe configura o chip e libera o acesso completo ao dashboard. Você será notificado por e-mail em cada etapa do processo.',
  },
];

// ============================================================
// MINI SIDEBAR — Apenas para a página de Parabéns
// ============================================================
function MiniSidebar({ user, planLabel, planColor }) {
  const navigate = useNavigate();
  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };
  return (
    <Box sx={{
      width: 240,
      bgcolor: '#0a1628',
      borderRight: '1px solid #1e293b',
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      position: 'fixed',
      left: 0,
      top: 0,
      height: '100vh',
      zIndex: 100,
    }}>
      {/* Logo */}
      <Box sx={{ px: 3, py: 3, borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, bgcolor: '#25d366', borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 900, fontSize: '1.1rem', color: '#0f172a'
        }}>C</Box>
        <Typography fontWeight={800} fontSize="0.95rem" color="white">Cetto Systems</Typography>
      </Box>

      {/* Menu */}
      <Box sx={{ flexGrow: 1, px: 2, pt: 3 }}>
        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, letterSpacing: '1px', px: 2, mb: 1, display: 'block' }}>
          NAVEGAÇÃO
        </Typography>
        {[
          { label: 'Dashboard', icon: <FiGrid size={16} />, path: '/dashboard', active: false },
          { label: 'Conversas', icon: <FiMessageSquare size={16} />, path: '/conversas', active: false },
          { label: 'Agentes', icon: <FiUsers size={16} />, path: '/agents', active: false },
        ].map(item => (
          <Box
            key={item.path}
            onClick={() => navigate(item.path)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2, py: 1.2, borderRadius: 2, mb: 0.5, cursor: 'pointer',
              color: '#94a3b8',
              '&:hover': { bgcolor: 'rgba(37,211,102,0.08)', color: '#25d366' },
              transition: 'all 0.2s',
            }}
          >
            {item.icon}
            <Typography fontSize="0.88rem" fontWeight={500}>{item.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Rodapé */}
      <Box sx={{ p: 2.5, borderTop: '1px solid #1e293b' }}>
        <Chip
          label={planLabel}
          size="small"
          sx={{ bgcolor: planColor.bg, color: planColor.text, fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.5px', height: 22, borderRadius: '6px', mb: 2 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ bgcolor: '#25d366', width: 34, height: 34, fontSize: '0.9rem', fontWeight: 700 }}>
            {user?.name?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap color="#f1f5f9" fontSize="0.85rem">
              {user?.name || 'Usuário'}
            </Typography>
            <Typography variant="caption" color="#64748b">Supervisor</Typography>
          </Box>
        </Box>
        <Box
          onClick={handleLogout}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
            borderRadius: 2, cursor: 'pointer', color: '#64748b',
            '&:hover': { bgcolor: 'rgba(239,68,68,0.08)', color: '#ef4444' },
            transition: 'all 0.2s',
          }}
        >
          <FiLogOut size={14} />
          <Typography fontSize="0.82rem" fontWeight={500}>Sair da conta</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [visibleItems, setVisibleItems] = useState([]);
  const [showContent, setShowContent] = useState(false);
  const [planInfo, setPlanInfo] = useState({ label: 'STARTER', color: { bg: '#334155', text: '#94a3b8' } });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Detecta o plano para o badge da sidebar
  useEffect(() => {
    const plan = (user?.plan || 'starter').toLowerCase();
    if (plan === 'business') setPlanInfo({ label: 'BUSINESS', color: { bg: '#25d366', text: '#0f172a' } });
    else if (plan === 'enterprise') setPlanInfo({ label: 'ENTERPRISE', color: { bg: '#6366f1', text: '#ffffff' } });
    else if (plan === 'plus') setPlanInfo({ label: 'PLUS', color: { bg: '#334155', text: '#94a3b8' } });
    else setPlanInfo({ label: 'STARTER', color: { bg: '#334155', text: '#94a3b8' } });
  }, [user?.plan]);

  // Animação do checklist
  useEffect(() => {
    CHECKLIST.forEach((item, index) => {
      setTimeout(() => {
        setVisibleItems(prev => [...prev, index]);
      }, item.delay);
    });
    setTimeout(() => setShowContent(true), 3000);
  }, []);

  // Meta Pixel: esta página NÃO dispara mais Purchase (out/26). A
  // assinatura volta do Stripe pro /register?step=3, que dispara a
  // ativação (CompleteRegistration + StartTrial/Purchase — ver Register.js);
  // aqui só chegaria o pacote de templates (/billing/buy-template-addon),
  // que nenhuma tela chama mais (template é pago direto à Meta). O Purchase
  // daqui saía com o valor do PLANO, uma vez por navegador, e qualquer um
  // que abrisse o endereço contava como compra.

  const planDisplayName = (user?.plan || 'Starter').charAt(0).toUpperCase() + (user?.plan || 'starter').slice(1).toLowerCase();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#0f172a' }}>
      {/* MINI SIDEBAR */}
      <MiniSidebar user={user} planLabel={planInfo.label} planColor={planInfo.color} />

      {/* CONTEÚDO PRINCIPAL */}
      <Box sx={{ flexGrow: 1, ml: { xs: 0, md: '240px' }, overflowY: 'auto' }}>
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>

          {/* ===== CABEÇALHO DE PARABÉNS ===== */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Box sx={{
              width: 110, height: 110, borderRadius: '50%',
              bgcolor: 'rgba(37,211,102,0.1)', border: '3px solid #25d366',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 3, fontSize: '3.5rem',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(37,211,102,0.4)' },
                '70%': { boxShadow: '0 0 0 24px rgba(37,211,102,0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(37,211,102,0)' },
              }
            }}>🎉</Box>

            <Chip
              label={`Plano ${planDisplayName} Ativado`}
              sx={{
                mb: 2,
                bgcolor: 'rgba(37,211,102,0.12)',
                color: '#25d366',
                border: '1px solid rgba(37,211,102,0.3)',
                fontWeight: 800,
                fontSize: '0.8rem',
                letterSpacing: '0.5px',
              }}
            />

            <Typography variant="h3" fontWeight={900} color="white" mb={1.5} lineHeight={1.2}>
              Parabéns, {user?.name?.split(' ')[0] || 'seja bem-vindo'}!
            </Typography>
            <Typography variant="h6" fontWeight={400} color="rgba(255,255,255,0.55)" maxWidth={520} mx="auto" lineHeight={1.6}>
              Você adquiriu o <strong style={{ color: '#25d366' }}>Plano {planDisplayName}</strong> e deu o primeiro passo para blindar a comunicação da sua empresa com a Meta.
            </Typography>
          </Box>

          {/* ===== CHECKLIST ANIMADO ===== */}
          <Box sx={{ bgcolor: '#1e293b', borderRadius: 3, p: 4, border: '1px solid rgba(255,255,255,0.07)', mb: 4 }}>
            <Typography variant="overline" color="#25d366" fontWeight={700} letterSpacing={1.5} display="block" mb={2.5}>
              O que acontece agora
            </Typography>
            <Stack spacing={2.5}>
              {CHECKLIST.map((item, index) => (
                <Fade key={index} in={visibleItems.includes(index)} timeout={500}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                      bgcolor: visibleItems.includes(index) ? 'rgba(37,211,102,0.15)' : 'rgba(255,255,255,0.04)',
                      border: `2px solid ${visibleItems.includes(index) ? '#25d366' : 'rgba(255,255,255,0.1)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.4s', fontSize: '0.85rem', color: '#25d366', fontWeight: 700,
                    }}>
                      {visibleItems.includes(index) ? '✓' : ''}
                    </Box>
                    <Typography
                      variant="body2"
                      color={visibleItems.includes(index) ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)'}
                      sx={{ transition: 'color 0.4s' }}
                    >
                      {item.label}
                    </Typography>
                  </Stack>
                </Fade>
              ))}
            </Stack>
          </Box>

          {/* ===== PRAZO ESTIMADO ===== */}
          <Box sx={{ bgcolor: 'rgba(59,130,246,0.07)', borderRadius: 2, p: 3, border: '1px solid rgba(59,130,246,0.2)', mb: 5 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Typography fontSize="1.4rem">⏱️</Typography>
              <Box>
                <Typography variant="body2" fontWeight={700} color="white" mb={0.5}>
                  Prazo estimado de ativação
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.6)" lineHeight={1.7}>
                  Nossa equipe revisará seus dados e iniciará a criação do App na Meta em até{' '}
                  <strong style={{ color: '#60a5fa' }}>24 horas úteis</strong>. O prazo de aprovação pela Meta pode variar entre{' '}
                  <strong style={{ color: '#60a5fa' }}>10 a 15 dias</strong> — fazemos tudo para agilizar ao máximo.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 5 }} />

          {/* ===== FAQ ===== */}
          <Fade in={showContent} timeout={800}>
            <Box>
              <Typography variant="h5" fontWeight={800} color="white" mb={0.5}>
                Perguntas Frequentes
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.4)" mb={3}>
                Tudo que você precisa saber sobre os próximos passos.
              </Typography>

              <Stack spacing={1.5} mb={5}>
                {FAQ.map((item, index) => (
                  <Accordion
                    key={index}
                    disableGutters
                    elevation={0}
                    sx={{
                      bgcolor: '#1e293b',
                      border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: '12px !important',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { border: '1px solid rgba(37,211,102,0.25)' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<FiChevronDown color="#94a3b8" />}
                      sx={{
                        px: 3, py: 0.5,
                        '& .MuiAccordionSummary-content': { my: 1.5 },
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} color="rgba(255,255,255,0.85)">
                        {item.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 2.5, pt: 0 }}>
                      <Typography variant="body2" color="rgba(255,255,255,0.55)" lineHeight={1.75}>
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>

              {/* ===== BOTÃO IR PARA DASHBOARD ===== */}
              <Fade in={showContent} timeout={1000}>
                <Stack spacing={2} maxWidth={480} mx="auto">
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={() => navigate('/dashboard')}
                    sx={{
                      bgcolor: '#25d366',
                      py: 2,
                      fontWeight: 800,
                      textTransform: 'none',
                      fontSize: '1.05rem',
                      borderRadius: 2,
                      letterSpacing: '0.2px',
                      boxShadow: '0 10px 30px rgba(37,211,102,0.25)',
                      '&:hover': { bgcolor: '#1db954', transform: 'translateY(-1px)', boxShadow: '0 14px 35px rgba(37,211,102,0.35)' },
                      transition: 'all 0.25s',
                    }}
                  >
                    Ir para o Dashboard →
                  </Button>
                  <Typography variant="caption" textAlign="center" color="rgba(255,255,255,0.25)">
                    Dúvidas? Entre em contato: contato@cettosystem.com.br
                  </Typography>
                </Stack>
              </Fade>
            </Box>
          </Fade>

        </Container>
      </Box>
    </Box>
  );
}
